import React from "react";
import { Box, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";
import moment from "moment";
import type { ITicketDetails } from "../type/ticketScanType";

export interface TicketPassCardProps {
	ticket: ITicketDetails | any;
	width?: number | string;
	isPrinting?: boolean;
}

export const getQrCodeUrl = (ticketNo: string, size = 360) => {
	return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
		ticketNo || ""
	)}`;
};

export const formatTicketDate = (dateStr?: string, createdAt?: string) => {
	if (dateStr) {
		const m = moment(dateStr);
		return m.isValid() ? m.format("DD MMM, YYYY") : dateStr;
	}
	if (createdAt) {
		const m = moment(createdAt);
		return m.isValid() ? m.format("DD MMM, YYYY") : createdAt;
	}
	return "N/A";
};

export const downloadTicketPassPNG = async (
	ticket: ITicketDetails | any,
	element?: HTMLElement | null
) => {
	if (!ticket) return;

	try {
		const target =
			element ||
			document.getElementById(`ticket-pass-card-${ticket?.id || ticket?.ticket_no}`) ||
			(document.querySelector(".ticket-pass-card-container") as HTMLElement);

		if (target) {
			const dataUrl = await toPng(target, {
				quality: 1,
				pixelRatio: 2,
				backgroundColor: "#ffffff",
				cacheBust: true,
				includeQueryParams: true,
			});
			saveAs(dataUrl, `BTTF-Pass-${ticket.ticket_no || "ticket"}.png`);
		}
	} catch (error) {
		console.error("Failed to download ticket pass:", error);
	}
};

const TicketPassCard = React.forwardRef<HTMLDivElement, TicketPassCardProps>(
	({ ticket, width = "100%", isPrinting = false }, ref) => {
		const qrUrl = getQrCodeUrl(ticket?.ticket_no || "", 360);
		const isPaid =
			(ticket?.payment_status || "").toLowerCase() === "paid" ||
			(ticket?.payment_status || "").toLowerCase() === "approved";

		const infoRows = [
			{
				label: "Visitor Name:",
				value: ticket?.visitor_name || "",
				color: "rgb(15, 23, 42)",
				isUppercase: false,
			},
			{
				label: "Visitor ID:",
				value: ticket?.visitor_no || ticket?.visitor_id || "",
				color: "rgb(15, 23, 42)",
				isUppercase: false,
			},
			{
				label: "Entry Persons:",
				value: `${ticket?.quantity || 1} ${Number(ticket?.quantity || 1) > 1 ? "Persons" : "Person"
					}`,
				color: "rgb(15, 23, 42)",
				isUppercase: false,
			},
			{
				label: "Payment Status:",
				value: ticket?.payment_status || "PAID",
				color: isPaid ? "#16a34a" : "#dc2626",
				isUppercase: true,
				fontWeight: 800,
			},
			{
				label: "Date:",
				value: formatTicketDate(ticket?.date, ticket?.created_at),
				color: "rgb(15, 23, 42)",
				isUppercase: false,
			},
		];

		return (
			<Box
				ref={ref}
				id={`ticket-pass-card-${ticket?.id || ticket?.ticket_no}`}
				className="ticket-pass-card-container visitor-ticket-pass-card"
				sx={{
					position: "relative",
					width: width || 330,
					maxWidth: 330,
					maxHeight: 550,
					aspectRatio: "500 / 845",
					borderRadius: isPrinting ? "0px" : "16px",
					overflow: "hidden",
					boxShadow: isPrinting ? "none" : "0 8px 30px rgba(0, 0, 0, 0.12)",
					bgcolor: "#ffffff",
					fontFamily: "'Outfit', 'Roboto', 'Helvetica', 'Arial', sans-serif",
					userSelect: "none",
					mx: "auto",
					containerType: "inline-size",
				}}
			>
				{/* Background template image */}
				<Box
					component="img"
					src="/ticket-pass-bg5.png"
					alt="BTTF-2026 Visitor Entry Pass"
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						objectFit: "fill",
						zIndex: 1,
						display: "block",
						pointerEvents: "none",
						marginTop: "6px"
					}}
				/>

				{/* Dynamic Overlay Content Layer */}
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						zIndex: 2,
					}}
				>
					{/* 1. QR Code Area (inside dashed box) */}
					<Box
						sx={{
							position: "absolute",
							top: "34.6%",
							left: "50%",
							transform: "translateX(-50%)",
							width: "38.5%",
							aspectRatio: "1 / 1",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							p: 0.5,
						}}
					>
						{ticket?.ticket_no ? (
							<Box
								component="img"
								src={qrUrl}
								alt={`QR for ${ticket.ticket_no}`}
								sx={{
									width: "94%",
									height: "94%",
									objectFit: "contain",
									display: "block",
									borderRadius: "6px",
								}}
							/>
						) : null}
					</Box>

					{/* 2. Ticket Number Pill */}
					<Box
						sx={{
							position: "absolute",
							top: "60.9%",
							left: "50%",
							transform: "translateX(-50%)",
							width: "44%",
							height: "4.5%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Typography
							sx={{
								fontWeight: 800,
								color: "#e2086f",
								fontSize: "4.8cqw",
								letterSpacing: "0.4px",
								fontFamily: "'Poppins', 'Jost', sans-serif",
								textAlign: "center",
								lineHeight: 1,
							}}
						>
							{ticket?.ticket_no || ""}
						</Typography>
					</Box>

					{/* 3. Visitor Info Dynamic Rows (Left: Key, Right: Value) */}
					<Box
						sx={{
							position: "absolute",
							top: "69.5%",
							left: "9%",
							right: "9%",
							bottom: "9.5%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							py: "0.5cqw",
						}}
					>
						{infoRows.map((row) => (
							<Box
								key={row.label}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
									gap: 1,
								}}
							>
								<Typography
									sx={{
										flexShrink: 0,
										fontWeight: 700,
										color: '#e2086f',
										fontSize: "3.0cqw",
										fontFamily: "'Poppins', sans-serif",
										lineHeight: 1.25,
										whiteSpace: "nowrap",
									}}
								>
									{row.label}
								</Typography>
								<Typography
									sx={{
										flex: 1,
										textAlign: "right",
										fontWeight: row.fontWeight || 700,
										color: row.color || "rgb(15, 23, 42)",
										fontSize: "3.0cqw",
										fontFamily: "'Poppins', sans-serif",
										lineHeight: 1,
										textTransform: row.isUppercase ? "uppercase" : "none",
										wordBreak: "break-word",
										maxWidth: "62%",
									}}
									title={typeof row.value === "string" ? row.value : undefined}
								>
									{row.value}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			</Box>
		);
	}
);

export default TicketPassCard;
